// Core imports
import { Fragment, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { t } from 'i18next';
import moment from 'moment-timezone';

// Component imports
import { Avatar, Button, Col, Collapse, Dropdown, Form, Input, Popconfirm, Row, Skeleton, Space, Table, Tooltip, Typography } from 'antd';
import { SearchOutlined, SyncOutlined, EyeOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import Permissioned from '../Permissioned';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import { BotIcon } from '../Icons/BotIcon';
import BotAvatar from '../UI/BotAvatar';
import BulkActionsModal from '../Modals/BulkActionsModal';
import AddNewKnowledgeBaseModal from '../Modals/AddNewKnowledgeBaseModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';
import KnowledgeBasesForm from '../Forms/KnowledgeBases/KnowledgeBasesForm';

// Misc imports
import { LANGUAGE_OPTIONS, PERMISSIONS_GROUP_EDITOR, PERMISSIONS_GROUP_VIEWER, DEFAULT_DATETIME_FORMAT, KNOWLEDGE_BASE_TYPES } from '../../constants/main';
import { INTEGRATION_TYPE } from '../../constants/integrations';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import { useDeleteKnowledgeBaseMutation, useDeleteKnowledgeBasesMutation, useFetchKnowledgeBasesQuery, useFullSyncKnowledgeBaseMutation, useSyncKnowledgeBaseMutation } from '../../store/knowledgeBases/knowledgeBasesApi';
import { useLazyFetchIntegrationQuery } from '../../store/integrations/integrationsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { NAV_ORGANIZATION_KNOWLEDGE_BASE } from '../../constants/navigation';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { truncate } from '../../utils/string/truncate';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { capitalize } from '../../utils/string/capitalize';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

// Constant declarations
const SPACE_TYPES = {
  EX: 'EX',
  CX: 'CX'
};
const BULK_ACTIONS = {
  SYNC: 'sync',
  DELETE: 'delete'
};

const MAX_CHARACTERS_DISPLAYED = 30;

const PAGE_SIZE = 20;

const KnowledgeBasesTable = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, orgId, knowledgeBaseId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { handleMouseDown, isDragging } = useDragDetection();
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [syncKB, { isLoading: isSyncingKnowledgeBase }] = useSyncKnowledgeBaseMutation();
  const [fullSyncKB, { isLoading: isFullSyncingKnowledgeBase }] = useFullSyncKnowledgeBaseMutation();
  const [fetchIntegration] = useLazyFetchIntegrationQuery();
  const { data: knowledgeBases, isFetching: isFetchingKnowledgeBases } = useFetchKnowledgeBasesQuery({ orgId, urlParams: {
    includeTenants: true,
    includeIntegration: true,
    page: currentPage - 1,
    pageSize: pageSize,
    ...(searchParams.get('type') && { type: searchParams.get('type') }),
    ...(searchParams.get('spaces') && { space: searchParams.get('spaces') }),
    ...(searchParams.get('bots') && { tenantIds: searchParams.get('bots') }),
    sortBy: searchParams.get('sortBy') || 'createdAt',
    order: searchParams.get('order') || 'desc',
    ...(searchParams.get('searchTerm') && { name: searchParams.get('searchTerm') })
  } });
  const { data: bots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const [deleteKnowledgeBase] = useDeleteKnowledgeBaseMutation();
  const [deleteKnowledgeBases, { isLoading: isDeletingKnowledgeBases }] = useDeleteKnowledgeBasesMutation();
  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);

  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState([]);
  const [knowledgeBasesToShow, setKnowledgeBasesToShow] = useState([]);
  const [bulkAction, setBulkAction] = useState();
  const [isBulkActionsKnowledgeBasesModalOpen, setIsBulkActionsKnowledgeBasesModalOpen] = useState(false);
  const [isAddKBModalOpen, setIsAddKBModalOpen] = useState(false);

  const hasEditAccess = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, false);

  const onRowSelectChange = (newSelectedKnowledgeBases) => {
    setSelectedKnowledgeBases(newSelectedKnowledgeBases);
  };

  const updateUrlSearchParams = (
    current = 1,
    pageSize = PAGE_SIZE,
    sorting = { createdAt: 'desc' },
    filters={
      bots: ((searchParams.get('bots') && decodeURIComponent(searchParams.get('bots'))?.split(','))) ?? [],
      type: [searchParams.get('type') ?? ''],
      space: [searchParams.get('spaces') ?? '']
    }
  ) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);

    const searchTerm = form.getFieldValue('searchTerm');
    urlSearchParams.set('page', current - 1);
    urlSearchParams.set('pageSize', pageSize);

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    if (filters?.type) {
      urlSearchParams.set('type', encodeURIComponent(filters.type[0]));
    } else {
      urlSearchParams.delete('type');
    }

    if (filters?.space) {
      urlSearchParams.set('spaces', encodeURIComponent(filters.space[0]));
    } else {
      urlSearchParams.delete('spaces');
    }

    if (filters?.bots) {
      urlSearchParams.set('bots', encodeURIComponent(filters.bots));
    } else {
      urlSearchParams.delete('bots');
    }

    if (searchTerm) {
      urlSearchParams.set('searchTerm', encodeURIComponent(searchTerm));
    } else {
      urlSearchParams.set('searchTerm', encodeURIComponent(''));
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const onKnowledgeBaseDelete = async (record) => {
    await deleteKnowledgeBase({ orgId: orgId, id: record.id });
  };

  const onKnowledgeBaseSync = async (record) => {
    let integrationType = record?.integration?.type;

    if (!integrationType && record?.source) {
      const integration = await fetchIntegration({ orgId, id: record.source }).unwrap();
      integrationType = integration?.type;
    }

    const fullSyncTypes = [INTEGRATION_TYPE.SHAREPOINT_KB, INTEGRATION_TYPE.AZURE_BLOB_STORAGE_KB];
    if (fullSyncTypes.includes(integrationType) && record?.source) {
      await fullSyncKB({ orgId, integrationId: record.source, knowledgeBaseId: record.id });
      return;
    }

    await syncKB({ orgId, id: record.id });
  };

  const handleBulkActionsMenuClick = (e) => {
    openBulkActionsKnowledgeBasesModal();
    setBulkAction(e.key);
  };

  const openBulkActionsKnowledgeBasesModal = () => {
    setKnowledgeBasesToShow(selectedKnowledgeBases.length > 5 ? 5 : selectedKnowledgeBases.length);
    setIsBulkActionsKnowledgeBasesModalOpen(true);
  };

  const closeBulkActionsKnowledgeBasesModal =(props) => {
    setIsBulkActionsKnowledgeBasesModalOpen(false);
  };

  const onBulkActionsKnowledgeBasesModalShowMore = () => {
    setKnowledgeBasesToShow(knowledgeBasesToShow + 5);
  };

  const handleBulkActionsKnowledgeBases = async () => {
    switch(bulkAction) {
      case BULK_ACTIONS.DELETE:
        await deleteKnowledgeBases({ orgId: orgId, urlParams: { ids: selectedKnowledgeBases } } );
        break;
      case BULK_ACTIONS.SYNC: {
        const selectedKnowledgeBaseRecords = knowledgeBases?.items?.filter(
          (kb) => selectedKnowledgeBases.includes(kb.id)
        ) || [];
        await Promise.all(selectedKnowledgeBaseRecords.map((kb) => onKnowledgeBaseSync(kb)));
        break;
      }
      default:
        break;
    }

    setSelectedKnowledgeBases([]);
    setIsBulkActionsKnowledgeBasesModalOpen(false);
  };

  const bulkActionsKnowledgeBasesModalData = () => {
    return selectedKnowledgeBases?.map((selectedKnowledgeBase) => {
      return knowledgeBases.items.find(item => item.id === selectedKnowledgeBase);
    });
  };

  const onAddKBModalOpen = () => {
    setIsAddKBModalOpen(true);
  };

  const onAddKBModalClose = () => {
    setIsAddKBModalOpen(false);
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);

  const handleTableChange = (pagination, filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'createdAt' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting, filters);
  };

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'createdAt';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  const columns = [
    {
      title: t('KB Name'),
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortingInfo.columnKey === 'name' && sortingInfo.order,
      render: record => {
        return record ? (
          <Space>
            <Tooltip title={record?.name?.length > MAX_CHARACTERS_DISPLAYED ? `${record?.name}` : ''}>
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchParams.get('searchTerm')]}
                autoEscape
                textToHighlight={record.name ? truncate(record.name, MAX_CHARACTERS_DISPLAYED, '...') : ''}
              />
            </Tooltip>
          </Space>
        ) : null;
      }
    },
    {
      title: t('Type'),
      key: 'type',
      filteredValue: searchParams.get('type') ? [searchParams.get('type')] : null,
      filters: [{
        text: 'Integration',
        value: KNOWLEDGE_BASE_TYPES.INTEGRATION
      }, {
        text: 'Internal',
        value: KNOWLEDGE_BASE_TYPES.INTERNAL
      }, {
        text: 'Shared',
        value: KNOWLEDGE_BASE_TYPES.SHARED
      }],
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      render: record => {
        return record?.type != null ? capitalize(record?.type) : t('Internal');
      }
    },
    {
      key: 'bots',
      title: t('Agents'),
      responsive: ['sm'],
      dataIndex: 'tenantAssociations',
      ellipsis: true,
      filterMultiple: true,
      filteredValue: searchParams.get('bots') ? decodeURIComponent(searchParams.get('bots'))?.split(',') : null,
      filterSearch: (input, record) => {
        const botDisplayName = bots.items.find((bot) => bot.id === record.value).displayName.toLowerCase();
        return botDisplayName.includes(input.toLowerCase());
      },
      filters: bots?.items.map(bot => ({
        text: (
          <Space size={4} align="center" wrap={false}>
            <Avatar
              src={bot?.image}
              size={24}
              shape="square"
              className="mr-2"
            >
              <BotIcon />
            </Avatar>

            <Tooltip
              title={bot?.displayName?.length >= 50 && bot?.displayName}
            >
              <Typography.Text ellipsis>
                {truncate(bot.displayName, 50, '...')}
              </Typography.Text>
            </Tooltip>
          </Space>
        ),
        value: bot.id
      })),
      filterIcon: useTableFilterIcon,
      render: record => {
        if (!record && isFetchingKnowledgeBases) {
          return <Skeleton.Button active size="small" />;
        }

        if (!record?.length > 0) {
          return '-';
        }
        const formattedRecord = record.map(tenant => tenant.tenantId);
        const associatedBots = bots?.items?.filter(bot => formattedRecord.includes(bot.id)) ?? [];

        return (
          <ResponsiveTagsList
            values={
              associatedBots.map((bot) => ({
                icon: <BotAvatar avatarSize={24} bot={bot} showBotName={false} />,
                label: bot.displayName,
                value: bot.id
              }))
            }
            maxTagCount={4}
            tagRender={(tag) => {
              const matchedTag = associatedBots.find(bot => bot.id === tag.value);
              return (
                <Tooltip overlayClassName="pointer-events-none" title={matchedTag.displayName}>
                  <BotAvatar avatarSize={24} bot={matchedTag} showBotName={false} className="mr-2" />
                </Tooltip>
              );
            }}
          />
        );
      }
    },
    {
      title: t('Languages'),
      key: 'languages',
      responsive: ['md'],
      render: record => {
        return (
          <ResponsiveTagsList
            values={
              record.languages.map((language) => ({
                icon: LANGUAGE_OPTIONS.find(lang => lang.key === language.languageCode)?.icon,
                label: LANGUAGE_OPTIONS.find(lang => lang.key === language.languageCode)?.label,
                value: language.languageCode
              }))
            }
            maxTagCount={4}
            tagRender={(tag) => {
              return (
                <Tooltip overlayClassName="pointer-events-none" title={tag.label}>
                  <Avatar
                    shape="square"
                    size={24}
                    className="flex-none bg-transparent"
                    icon={LANGUAGE_OPTIONS.find(lang => lang.key === tag.value)?.icon}
                  />
                </Tooltip>
              );
            }}
          />
        );
      }
    },
    {
      title: t('Related Space'),
      key: 'space',
      responsive: ['md'],
      filteredValue: searchParams.get('spaces') ? [searchParams.get('spaces')] : null,
      filters: Object.values(SPACE_TYPES).map((type) => {
        return {
          text: type,
          value: type
        };
      }),
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      render: record => {
        if (!record.spaces || isEmptyArray(record.spaces)) {
          return '-';
        }

        return (
          <ResponsiveTagsList
            values={record.spaces.map((space, index) => ({ label: space.toUpperCase(), value: index }))}
            mode="tags"
            className="w-100"
            tagRender={(tag) => <span className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</span>}
          />
        );
      }
    },
    {
      title: t('Number of Articles'),
      key: 'totalArticles',
      dataIndex: 'totalArticles',
      responsive: ['md'],
      render: totalArticles => {
        if (totalArticles) {
          return totalArticles;
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Updated at'),
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      responsive: ['sm'],
      sorter: (a, b) => a.updatedAt - b.updatedAt,
      sortOrder: sortingInfo.columnKey === 'updatedAt' && sortingInfo.order,
      render: updatedAt => {
        if (updatedAt) {
          return moment(updatedAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 215,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        const disableEdit = record.type === KNOWLEDGE_BASE_TYPES.SHARED;
        return (
          <Space onClick={(e) => e.stopPropagation()}>
            <Tooltip overlayClassName="pointer-events-none" title={disableEdit ? t('Shared KBs cannot be edited') : hasEditAccess ? t('Edit KB') : t('View KB')}>
              <Button
                type="text"
                shape="circle"
                icon={hasEditAccess ? <EditIcon /> : <EyeOutlined/>}
                disabled={disableEdit}
                onClick={() => navigate(`${replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASE, { orgId: orgId, knowledgeBaseId: record.id })}`)}
              />
            </Tooltip>

            <Tooltip overlayClassName="pointer-events-none" title={t('Sync KB')}>
              <Button
                hidden={!hasEditAccess}
                shape="circle"
                type="text"
                icon={<SyncOutlined />}
                loading={isFetchingKnowledgeBases || isSyncingKnowledgeBase || isFullSyncingKnowledgeBase}
                disabled={record.type !== KNOWLEDGE_BASE_TYPES.INTEGRATION}
                onClick={() => onKnowledgeBaseSync(record)}
              />
            </Tooltip>

            <Popconfirm
              title={t('Delete Knowledge Base?')}
              okText={t('Yes')}
              cancelText={t('No')}
              placement="topRight"
              onConfirm={() => {
                onKnowledgeBaseDelete(record);
              }}
            >
              <Tooltip overlayClassName="pointer-events-none" title={t('Delete KB')}>
                <Button
                  hidden={!hasEditAccess}
                  shape="circle"
                  type="text"
                  icon={<DeleteIcon />}
                  loading={isFetchingKnowledgeBases}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  const rowSelection = {
    selectedRowKeys: selectedKnowledgeBases,
    onChange: onRowSelectChange,
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => onDropdownSelect('all')
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => onDropdownSelect('none')
      }
    ]
  };

  const onDropdownSelect = (key) => {
    let newSelectedKnowledgeBases = [];
    if (key === 'all') {
      newSelectedKnowledgeBases = knowledgeBases?.items?.map((item) => item.name);
    }
    setSelectedKnowledgeBases(newSelectedKnowledgeBases);
  };

  return (
    <Fragment>
      <Collapse
        ghost
        accordion
        activeKey={knowledgeBaseId ? 1 : 0}
        destroyInactivePanel
      >
        <Collapse.Panel
          key={0}
          showArrow={false}
          className="ant-collapse-p-0"
          forceRender
        >
          <Row gutter={24} className="mb-2">
            <Col flex="auto">
              <Space className="flex-wrap mb-2">
                <Form
                  name="knowledgeBasesForm"
                  className="align-items-center"
                  initialValues={{
                    searchTerm: searchParams.get('searchTerm') ? decodeURIComponent(searchParams.get('searchTerm')) : ''
                  }}
                  form={form}
                >
                  <Form.Item
                    name="searchTerm"
                  >
                    <Input
                      size="large"
                      prefix={
                        <SearchOutlined
                          className="text-secondary"
                        />
                      }
                      placeholder={t('Search in contents')}
                      allowClear
                      onChange={(e) => {
                        debouncedOnChange();
                        form.setFieldsValue({ search: e.target.value });
                      }}
                    />
                  </Form.Item>
                </Form>
              </Space>
            </Col>

            <Col>
              <Space>
                <Permissioned
                  allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                  currentPermissions={userPermissions}
                  fallbackComponent={null}
                >
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      onClick: handleBulkActionsMenuClick,
                      items: [
                        {
                          label: t('Delete Selected'),
                          key: BULK_ACTIONS.DELETE
                        },
                        {
                          label: t('Sync Selected'),
                          key: BULK_ACTIONS.SYNC
                        }
                      ]
                    }}
                    disabled={!selectedKnowledgeBases.length > 0}
                  >
                    <Button
                      block
                      size="large"
                    >
                      {t('Bulk Actions')}
                      <ArrowDownIcon />
                    </Button>
                  </Dropdown>
                </Permissioned>

                <Permissioned
                  allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                  currentPermissions={userPermissions}
                  fallbackComponent={null}
                >
                  <Button
                    icon={<PlusIcon />}
                    type="primary"
                    className="float-right"
                    size="large"
                    onClick={onAddKBModalOpen}
                    loading={isFetchingKnowledgeBases}
                  >
                    {t('Add New KB')}
                  </Button>
                </Permissioned>
              </Space>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Table
                rowSelection={hasEditAccess ? rowSelection : null}
                size="small"
                rowKey="id"
                onRow={(record) => ({
                  onMouseDown: handleMouseDown,
                  onClick: () => {
                    if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission))) {
                      navigate(`${replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASE, { orgId: orgId, knowledgeBaseId: record.id })}`);
                    }
                  }
                })}
                rowClassName={(record) => record.syncedAt ? '' : 'cursor-pointer'}
                columns={columns}
                dataSource={knowledgeBases?.items}
                loading={isFetchingKnowledgeBases}
                onChange={handleTableChange}
                pagination={{
                  total: knowledgeBases?.total || 0,
                  defaultCurrent: 1,
                  current: currentPage,
                  pageSize: knowledgeBases?.pageSize || 0,
                  showSizeChanger: true,
                  size: 'default',
                  showTotal: (total) => `${t('Total Knowledge Bases')}: ${total}`
                }}
              />
            </Col>
          </Row>
        </Collapse.Panel>

        <Collapse.Panel
          key={1}
          showArrow={false}
          className="ant-collapse-p-0"
        >
          <KnowledgeBasesForm key={knowledgeBaseId} knowledgeBaseId={knowledgeBaseId} />
        </Collapse.Panel>
      </Collapse>

      <BulkActionsModal
        title={`${selectedKnowledgeBases.length === 1 ? 'knowledge base' : 'knowledge bases'}`}
        action={bulkAction}
        isModalOpen={isBulkActionsKnowledgeBasesModalOpen}
        data={bulkActionsKnowledgeBasesModalData()}
        onModalClose={closeBulkActionsKnowledgeBasesModal}
        onModalConfirm={handleBulkActionsKnowledgeBases}
        loader={isDeletingKnowledgeBases || isSyncingKnowledgeBase || isFullSyncingKnowledgeBase}
        onShowMore={onBulkActionsKnowledgeBasesModalShowMore}
        entityCount={knowledgeBasesToShow}
        displayProperty="name"
      />

      <AddNewKnowledgeBaseModal
        isModalOpen={isAddKBModalOpen}
        closeModal={onAddKBModalClose}
      />

    </Fragment>
  );
};

export default KnowledgeBasesTable;
